import React, { useState, useCallback } from 'react';
import { Input, InputNumber, Modal, Spin, Select, Tabs, Table, Checkbox, Radio, message } from 'antd';
import {
  PlusCircleOutlined, MinusCircleOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVendorEvalForm, FIXED_CONTACTS } from './hooks/useVendorEvalForm';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import LookupField from '../../components/LookupField';
import vendorEvalApi from '../../services/vendorEvalApi';
import './P110HForm.css';

const { TextArea } = Input;
const { TabPane } = Tabs;

const CATEGORY_OPTIONS = [
  { value: 'D', label: 'D - 廠商' },
  { value: 'CD', label: 'CD - 客戶兼廠商' },
  { value: 'F', label: 'F - 銀行' },
  { value: 'M', label: 'M - 總務' },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: 'A', label: 'A - 現金' },
  { value: 'B', label: 'B - 支票' },
  { value: 'C', label: 'C - 匯款' },
  { value: 'D', label: 'D - 信用狀' },
];

const P110HForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const {
    mode, loading,
    f10h_01, f10h_02, f10h_03, f10h_10, f10h_12, f10h_13,
    f10h_14,
    f10h_21, f10h_22, f10h_23, f10h_24,
    f10h_30, f10h_34,
    f10h_41, f10h_411, f10h_42, f10h_421, f10h_43, f10h_431,
    f10h_44, f10h_441, f10h_45, f10h_451,
    f10h_46, f10h_47, f10h_48, f10h_49,
    f10h_51,
    f10h_80, f10h_802, f10h_81, f10h_812, f10h_82, f10h_822,
    setF10h01, setF10h02, setF10h10, setF10h12, setF10h13,
    setF10h14,
    setF10h21, setF10h22, setF10h23, setF10h24,
    setF10h30, setF10h34,
    setF10h41, setF10h411, setF10h42, setF10h421,
    setF10h43, setF10h431, setF10h44, setF10h441,
    setF10h45, setF10h451,
    setF10h46, setF10h47, setF10h48, setF10h49,
    setF10h51,
    addresses, setAddresses, contacts, setContacts, credit, setCredit,
    addContact, removeContact,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch, recordPosition, recordCount,
  } = useVendorEvalForm();

  const [searchKey, setSearchKey] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [selectedContactIdx, setSelectedContactIdx] = useState<number>(0);

  // Load review record if ?review= is present
  React.useEffect(() => {
    if (reviewId) {
      quickSearch(reviewId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId]);
  const isEditing = mode === 'add' || mode === 'edit';

  const updateAddress = (idx: number, field: string, value: string) => {
    const newAddr = [...addresses];
    newAddr[idx] = { ...newAddr[idx], [field]: value };
    setAddresses(newAddr);
  };

  const updateContact = (idx: number, field: string, value: string) => {
    const newContacts = [...contacts];
    newContacts[idx] = { ...newContacts[idx], [field]: value };
    setContacts(newContacts);
  };

  const updateCredit = (field: string, value: string | number) => {
    setCredit({ ...credit, [field]: value });
  };

  // Approval state
  const approval: ApprovalState = {
    statusCode: f10h_80 || '',
    isApproved: f10h_80 === 'P',
    isPending: ['D', 'S'].includes(f10h_80 || ''), // 統一碼 D + 舊碼 S 過渡容忍
    isLocked: ['P', 'D', 'S'].includes(f10h_80 || ''),
  };

  // Approval handlers
  const handleSubmit = useCallback(() => {
    if (!f10h_01 || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將廠商評鑑 ${f10h_01} 送審？`,
      okText: '送審', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await vendorEvalApi.submitForApproval(f10h_01);
          message.success(resp.message || '送審成功');
          quickSearch(f10h_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗'));
        }
      },
    });
  }, [f10h_01, isEditing, quickSearch]);

  const handleApprove = useCallback(() => {
    if (!f10h_01 || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准廠商評鑑 ${f10h_01}？`,
      okText: '核准', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await vendorEvalApi.approve(f10h_01);
          message.success(resp.message || '核准成功');
          quickSearch(f10h_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗'));
        }
      },
    });
  }, [f10h_01, isEditing, quickSearch]);

  const handleReject = useCallback(() => {
    if (!f10h_01 || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回廠商評鑑 ${f10h_01}？`,
      okText: '退回', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await vendorEvalApi.reject(f10h_01);
          message.success(resp.message || '退回成功');
          quickSearch(f10h_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗'));
        }
      },
    });
  }, [f10h_01, isEditing, quickSearch]);

  const handleWriteoff = useCallback(() => {
    if (!f10h_01 || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${f10h_01}？沖銷後可重新送審。`,
      okText: '沖銷', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await vendorEvalApi.writeoff(f10h_01);
          message.success(resp.message || '沖銷成功');
          quickSearch(f10h_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗'));
        }
      },
    });
  }, [f10h_01, isEditing, quickSearch]);

  const handleDelete = useCallback(() => {
    if (!f10h_01 || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除廠商 ${f10h_01} (${f10h_12}) 嗎？`,
      onOk: doDelete,
    });
  }, [f10h_01, f10h_12, isEditing, doDelete]);

  // --- Address table columns ---
  const addressColumns = [
    {
      title: '類別', dataIndex: 'f10h1_11', key: 'f10h1_11', width: 80,
      render: (val: string) => val || '',
    },
    {
      title: '地址', dataIndex: 'f10h1_12', key: 'f10h1_12',
      render: (val: string, _rec: any, idx: number) =>
        isEditing
          ? <Input value={val || ''} onChange={(e) => updateAddress(idx, 'f10h1_12', e.target.value)} />
          : (val || ''),
    },
    {
      title: '電話', dataIndex: 'f10h1_13', key: 'f10h1_13', width: 180,
      render: (val: string, _rec: any, idx: number) =>
        isEditing
          ? <Input value={val || ''} onChange={(e) => updateAddress(idx, 'f10h1_13', e.target.value)} />
          : (val || ''),
    },
    {
      title: '傳真', dataIndex: 'f10h1_14', key: 'f10h1_14', width: 180,
      render: (val: string, _rec: any, idx: number) =>
        isEditing
          ? <Input value={val || ''} onChange={(e) => updateAddress(idx, 'f10h1_14', e.target.value)} />
          : (val || ''),
    },
  ];

  // --- Contact table columns (Issue 4 + Issue 5: 6 columns including duplicate 公司電話) ---
  const contactColumns = [
    {
      title: '職務', dataIndex: 'f10h2_11', key: 'f10h2_11', width: 80,
      render: (val: string, _rec: any, idx: number) => {
        const isFixed = FIXED_CONTACTS.includes(val || '');
        return isEditing && !isFixed
          ? <Input value={val || ''} onChange={(e) => updateContact(idx, 'f10h2_11', e.target.value)} style={{ width: 70 }} />
          : (val || '');
      },
    },
    {
      title: '職稱', dataIndex: 'f10h2_12', key: 'f10h2_12', width: 100,
      render: (val: string, _rec: any, idx: number) =>
        isEditing
          ? <Input value={val || ''} onChange={(e) => updateContact(idx, 'f10h2_12', e.target.value)} style={{ width: 90 }} />
          : (val || ''),
    },
    {
      title: '姓名', dataIndex: 'f10h2_02', key: 'f10h2_02', width: 100,
      render: (val: string, _rec: any, idx: number) =>
        isEditing
          ? <Input value={val || ''} onChange={(e) => updateContact(idx, 'f10h2_02', e.target.value)} style={{ width: 90 }} />
          : (val || ''),
    },
    {
      title: '專線', dataIndex: 'f10h2_13', key: 'f10h2_13_direct', width: 130,
      render: (val: string, _rec: any, idx: number) =>
        isEditing
          ? <Input value={val || ''} onChange={(e) => updateContact(idx, 'f10h2_13', e.target.value)} style={{ width: 120 }} />
          : (val || ''),
    },
    {
      title: '公司電話', key: 'f10h2_13_company', width: 130,
      render: (_val: any, rec: any) => rec.f10h2_13 || '',
    },
    {
      title: '分機', dataIndex: 'f10h2_14', key: 'f10h2_14', width: 80,
      render: (val: string, _rec: any, idx: number) =>
        isEditing
          ? <Input value={val || ''} onChange={(e) => updateContact(idx, 'f10h2_14', e.target.value)} style={{ width: 70 }} />
          : (val || ''),
    },
  ];

  // Selected contact for detail section
  const selectedContact = contacts[selectedContactIdx] || contacts[0];

  return (
    <Spin spinning={loading}>
      <div className="p110h-form">
        <h2>廠商評鑑單</h2>

        {/* Quick Search */}
        <div className="p110h-search-bar">
          <Input
            placeholder="輸入代號或簡稱搜尋"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            onPressEnter={() => quickSearch(searchKey)}
            style={{ width: 200 }}
            disabled={isEditing}
          />
          <button className="sab-btn" onClick={() => quickSearch(searchKey)} disabled={isEditing}>
            <SearchOutlined /> 搜尋
          </button>
          <span className="p110h-record-info">{recordPosition} / {recordCount}</span>
        </div>

        {/* Issue 1: Form-level header fields (above tabs) */}
        <div className="p110h-form-header">
          <div className="p110h-field-row">
            <label>廠商評鑑單號</label>
            <Input value={f10h_01} onChange={(e) => setF10h01(e.target.value)}
              maxLength={14} disabled={mode !== 'add'} style={{ width: 140 }} />
          </div>
          <div className="p110h-field-row">
            <label>公司代號</label>
            <Input value={f10h_10} onChange={(e) => setF10h10(e.target.value)}
              maxLength={12} disabled={!isEditing || mode === 'edit'} style={{ width: 140 }} />
          </div>
          <div className="p110h-field-row">
            <label>類別</label>
            <Select value={f10h_02} onChange={(v) => setF10h02(v)}
              options={CATEGORY_OPTIONS} disabled={!isEditing} style={{ width: 160 }} />
          </div>
          <div className="p110h-field-row">
            <label>申請日期</label>
            <Input value={f10h_03 ? f10h_03.substring(0, 10) : ''} disabled style={{ width: 140 }} />
          </div>
          <div className="p110h-field-row">
            <label>全名</label>
            <Input value={f10h_12} onChange={(e) => setF10h12(e.target.value)}
              maxLength={150} disabled={!isEditing} style={{ width: 200 }} />
          </div>
          <div className="p110h-field-row">
            <label>經辦人員</label>
            <LookupField lookupType="employee" value={f10h_30 || ''}
              onChange={(code) => setF10h30(code)}
              disabled={!isEditing} title="選擇經辦人員" />
          </div>
        </div>

        <Tabs defaultActiveKey="1">
          {/* Tab 1: Basic Info + Addresses (Issue 2: merged address tab into tab 1) */}
          <TabPane tab="基本資料" key="1">
            <div className="p110h-fields">
              <div className="p110h-field-row">
                <label>英文名稱</label>
                <LookupField lookupType="vendorEnglishName" value={f10h_13 || ''}
                  onChange={(code) => setF10h13(code)}
                  disabled={!isEditing} title="選擇英文名稱" />
              </div>
              <div className="p110h-field-row">
                <label>國碼</label>
                <LookupField lookupType="countryCode" value={f10h_14 || ''}
                  onChange={(code) => setF10h14(code)}
                  disabled={!isEditing} title="選擇國碼" />
              </div>
              <div className="p110h-field-row">
                <label>統一編號</label>
                <Input value={f10h_21} onChange={(e) => setF10h21(e.target.value)}
                  maxLength={15} disabled={!isEditing} style={{ width: 160 }} />
              </div>
              <div className="p110h-field-row">
                <label>公司電子郵件</label>
                <Input value={f10h_22} onChange={(e) => setF10h22(e.target.value)}
                  maxLength={40} disabled={!isEditing} style={{ width: 300 }} />
              </div>
              <div className="p110h-field-row">
                <label>網站</label>
                <Input value={f10h_23} onChange={(e) => setF10h23(e.target.value)}
                  maxLength={40} disabled={!isEditing} style={{ width: 300 }} />
              </div>
              <div className="p110h-field-row">
                <label>票款別</label>
                <Select value={f10h_51 || undefined} onChange={(v) => setF10h51(v)}
                  options={PAYMENT_TYPE_OPTIONS} disabled={!isEditing} style={{ width: 160 }}
                  placeholder="請選擇票款別" allowClear />
              </div>
              {f10h_02 === 'F' && (
                <div className="p110h-field-row">
                  <label>帳號(銀行用)</label>
                  <Input value={f10h_34} onChange={(e) => setF10h34(e.target.value)}
                    maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
                </div>
              )}
            </div>

            {/* Issue 3: Address table (moved from old tab 2, now an Ant Design Table) */}
            <div className="p110h-section">
              <h4>地址明細</h4>
              <Table
                dataSource={addresses.map((a, i) => ({ ...a, key: i }))}
                columns={addressColumns}
                pagination={false}
                size="small"
                bordered
              />
            </div>
          </TabPane>

          {/* Tab 2: Contacts (Issue 4 + 5 + 6: Table + detail section) */}
          <TabPane tab="連絡人" key="2">
            <div className="p110h-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0 }}>連絡人明細</h4>
                {isEditing && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="sab-btn" onClick={addContact}>
                      <PlusCircleOutlined /> 人員新增
                    </button>
                  </div>
                )}
              </div>

              <Table
                dataSource={contacts.map((c, i) => ({ ...c, key: i }))}
                columns={[
                  ...contactColumns,
                  ...(isEditing ? [{
                    title: '', key: 'action', width: 40,
                    render: (_val: any, rec: any, idx: number) => {
                      const isFixed = FIXED_CONTACTS.includes(rec.f10h2_11 || '');
                      return !isFixed ? (
                        <button
                          className="sab-btn sab-btn-danger"
                          style={{ padding: '2px 6px' }}
                          onClick={(e) => { e.stopPropagation(); removeContact(idx); }}
                        >
                          <MinusCircleOutlined />
                        </button>
                      ) : null;
                    },
                  }] : []),
                ]}
                pagination={false}
                size="small"
                bordered
                rowClassName={(_rec, idx) => idx === selectedContactIdx ? 'ant-table-row-selected' : ''}
                onRow={(_rec, idx) => ({
                  onClick: () => { if (idx !== undefined) setSelectedContactIdx(idx); },
                  style: { cursor: 'pointer' },
                })}
              />

              {/* Issue 6: Contact detail section for selected row */}
              {selectedContact && (
                <div className="p110h-contact-detail">
                  <h4 style={{ margin: '0 0 8px', color: '#666' }}>
                    聯絡人詳細資料 — {selectedContact.f10h2_11 || ''} {selectedContact.f10h2_02 || ''}
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div className="p110h-field-row">
                      <label>姓名</label>
                      <Input value={selectedContact.f103_01 || ''} disabled style={{ width: 120 }} />
                    </div>
                    <div className="p110h-field-row">
                      <label>性別</label>
                      <Radio.Group value={selectedContact.f103_14 || ''} disabled={!isEditing}
                        onChange={(e) => updateContact(selectedContactIdx, 'f103_14', e.target.value)}>
                        <Radio value="M">男</Radio>
                        <Radio value="F">女</Radio>
                      </Radio.Group>
                    </div>
                    <div className="p110h-field-row">
                      <label>英文姓名</label>
                      <Input value={selectedContact.f103_12 || ''} disabled={!isEditing}
                        onChange={(e) => updateContact(selectedContactIdx, 'f103_12', e.target.value)}
                        style={{ width: 150 }} maxLength={30} />
                    </div>
                    <div className="p110h-field-row">
                      <label>行動電話</label>
                      <Input value={selectedContact.f103_22 || ''} disabled={!isEditing}
                        onChange={(e) => updateContact(selectedContactIdx, 'f103_22', e.target.value)}
                        style={{ width: 150 }} maxLength={16} />
                    </div>
                    <div className="p110h-field-row">
                      <label>聯絡電話</label>
                      <Input value={selectedContact.f103_21 || ''} disabled={!isEditing}
                        onChange={(e) => updateContact(selectedContactIdx, 'f103_21', e.target.value)}
                        style={{ width: 150 }} maxLength={20} />
                    </div>
                    <div className="p110h-field-row">
                      <label>電子郵件</label>
                      <Input value={selectedContact.f10h2_15 || ''} disabled={!isEditing}
                        onChange={(e) => updateContact(selectedContactIdx, 'f10h2_15', e.target.value)}
                        style={{ width: 200 }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabPane>

          {/* Tab 3: Credit / Summary (Issue 7: label fix) */}
          <TabPane tab="信用狀況/摘要" key="3">
            <div className="p110h-section">
              <div className="p110h-field-row" style={{ marginBottom: 12 }}>
                <label>幣別</label>
                <Input value={credit.f10h3_11 || ''} disabled style={{ width: 80 }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* LEFT: Customer */}
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
                  <h4 style={{ margin: '0 0 8px', color: '#1890ff' }}>客戶（受訂/出貨）</h4>
                  <div className="p110h-field-row">
                    <label>受訂信用額度</label>
                    <InputNumber value={credit.f10h3_31 || 0} disabled style={{ width: 120 }} />
                  </div>
                  <div className="p110h-field-row">
                    <label>已用額度</label>
                    <InputNumber value={credit.f10h3_32 || 0} disabled style={{ width: 120 }} />
                  </div>
                  <div className="p110h-field-row">
                    <label>擔保方式</label>
                    <Input value={credit.f10h3_35 || ''} disabled style={{ width: 120 }} />
                  </div>
                  <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />
                  <div className="p110h-field-row">
                    <label>出貨信用額度</label>
                    <InputNumber value={credit.f10h3_36 || 0} disabled style={{ width: 120 }} />
                  </div>
                  <div className="p110h-field-row">
                    <label>已用額度</label>
                    <InputNumber value={credit.f10h3_37 || 0} disabled style={{ width: 120 }} />
                  </div>
                  <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />
                  <div className="p110h-field-row">
                    <label>收款條件</label>
                    <LookupField lookupType="paymentTerms" value={credit.f10h3_13 as string || ''}
                      onChange={(code) => updateCredit('f10h3_13', code)}
                      disabled={!isEditing || (f10h_02 === 'D')} title="選擇收款條件" />
                    <span style={{ color: '#888', fontSize: 12 }}>{credit.f10h3_131 || ''}</span>
                  </div>
                  <div className="p110h-field-row">
                    <label>收款銀行</label>
                    <Radio.Group value={credit.f10h3_171 || 0} disabled={!isEditing}
                      onChange={(e) => updateCredit('f10h3_171', e.target.value)}>
                      <Radio value={1}>銀行1</Radio>
                      <Radio value={2}>銀行2</Radio>
                    </Radio.Group>
                  </div>
                  <div className="p110h-field-row">
                    <label>往來銀行</label>
                    <Input value={credit.f10h3_17 || ''} disabled={!isEditing}
                      onChange={(e) => updateCredit('f10h3_17', e.target.value)} style={{ width: 140 }} maxLength={30} />
                  </div>
                  <div className="p110h-field-row">
                    <label>帳號</label>
                    <Input value={credit.f10h3_172 || ''} disabled={!isEditing}
                      onChange={(e) => updateCredit('f10h3_172', e.target.value)} style={{ width: 140 }} maxLength={30} />
                  </div>
                </div>

                {/* RIGHT: Vendor */}
                <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
                  <h4 style={{ margin: '0 0 8px', color: '#52c41a' }}>廠商（採購/進貨）</h4>
                  <div className="p110h-field-row">
                    <label>採購信用額度</label>
                    <InputNumber value={credit.f10h3_21 || 0} disabled style={{ width: 120 }} />
                  </div>
                  <div className="p110h-field-row">
                    <label>已用額度</label>
                    <InputNumber value={credit.f10h3_22 || 0} disabled style={{ width: 120 }} />
                  </div>
                  <div className="p110h-field-row">
                    <label>擔保方式</label>
                    <Input value={credit.f10h3_25 || ''} disabled style={{ width: 120 }} />
                  </div>
                  <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />
                  <div className="p110h-field-row">
                    <label>進貨信用額度</label>
                    <InputNumber value={credit.f10h3_26 || 0} disabled style={{ width: 120 }} />
                  </div>
                  <div className="p110h-field-row">
                    <label>已用額度</label>
                    <InputNumber value={credit.f10h3_27 || 0} disabled style={{ width: 120 }} />
                  </div>
                  <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />
                  <div className="p110h-field-row">
                    <label>付款條件</label>
                    <LookupField lookupType="paymentTerms" value={credit.f10h3_14 as string || ''}
                      onChange={(code) => updateCredit('f10h3_14', code)}
                      disabled={!isEditing || (f10h_02 === 'C')} title="選擇付款條件" />
                    <span style={{ color: '#888', fontSize: 12 }}>{credit.f10h3_141 || ''}</span>
                  </div>
                  <div className="p110h-field-row">
                    <label>付款銀行</label>
                    <Radio.Group value={credit.f10h3_161 || 0} disabled={!isEditing}
                      onChange={(e) => updateCredit('f10h3_161', e.target.value)}>
                      <Radio value={1}>銀行1</Radio>
                      <Radio value={2}>銀行2</Radio>
                    </Radio.Group>
                  </div>
                  <div className="p110h-field-row">
                    <label>往來銀行</label>
                    <Input value={credit.f10h3_16 || ''} disabled={!isEditing}
                      onChange={(e) => updateCredit('f10h3_16', e.target.value)} style={{ width: 140 }} maxLength={30} />
                  </div>
                  <div className="p110h-field-row">
                    <label>帳號</label>
                    <Input value={credit.f10h3_162 || ''} disabled={!isEditing}
                      onChange={(e) => updateCredit('f10h3_162', e.target.value)} style={{ width: 140 }} maxLength={30} />
                  </div>
                </div>
              </div>

              <h4 style={{ marginTop: 16 }}>備註/摘要</h4>
              <TextArea value={f10h_24} onChange={(e) => setF10h24(e.target.value)}
                disabled={!isEditing} rows={4} />
            </div>
          </TabPane>

          {/* Tab 4: Other (Issue 7: label "其他" → "其它") */}
          <TabPane tab="其它" key="4">
            <div className="p110h-section">
              <div className="p110h-field-row" style={{ marginBottom: 8 }}>
                <label>廠商特性</label>
                <LookupField lookupType="remarkTemplate" value={f10h_411 || ''}
                  onChange={(code) => setF10h411(code)}
                  disabled={!isEditing} title="選擇廠商特性代碼" />
              </div>
              <TextArea value={f10h_41} onChange={(e) => setF10h41(e.target.value)}
                disabled={!isEditing} rows={3} style={{ maxWidth: 500, marginBottom: 16 }} />

              <div className="p110h-field-row" style={{ marginBottom: 8 }}>
                <label>機器設備簡介</label>
                <LookupField lookupType="remarkTemplate" value={f10h_421 || ''}
                  onChange={(code) => setF10h421(code)}
                  disabled={!isEditing} title="選擇機器設備代碼" />
              </div>
              <TextArea value={f10h_42} onChange={(e) => setF10h42(e.target.value)}
                disabled={!isEditing} rows={3} style={{ maxWidth: 500, marginBottom: 16 }} />

              <div className="p110h-field-row" style={{ marginBottom: 8 }}>
                <label>產能預估</label>
                <Input value={f10h_431} onChange={(e) => setF10h431(e.target.value)}
                  maxLength={2} disabled={!isEditing} style={{ width: 60 }} placeholder="代碼" />
              </div>
              <TextArea value={f10h_43} onChange={(e) => setF10h43(e.target.value)}
                disabled={!isEditing} rows={3} style={{ maxWidth: 500, marginBottom: 16 }} />

              <div className="p110h-field-row" style={{ marginBottom: 8 }}>
                <label>樣品評核</label>
                <Input value={f10h_441} onChange={(e) => setF10h441(e.target.value)}
                  maxLength={2} disabled={!isEditing} style={{ width: 60 }} placeholder="代碼" />
              </div>
              <TextArea value={f10h_44} onChange={(e) => setF10h44(e.target.value)}
                disabled={!isEditing} rows={3} style={{ maxWidth: 500, marginBottom: 16 }} />

              <div className="p110h-field-row" style={{ marginBottom: 8 }}>
                <label>其他</label>
                <Input value={f10h_451} onChange={(e) => setF10h451(e.target.value)}
                  maxLength={2} disabled={!isEditing} style={{ width: 60 }} placeholder="代碼" />
              </div>
              <TextArea value={f10h_45} onChange={(e) => setF10h45(e.target.value)}
                disabled={!isEditing} rows={3} style={{ maxWidth: 500, marginBottom: 16 }} />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                <Checkbox checked={f10h_46} onChange={(e) => setF10h46(e.target.checked)} disabled={!isEditing}>
                  經 ISO 9000 認證
                </Checkbox>
                <Checkbox checked={f10h_47} onChange={(e) => setF10h47(e.target.checked)} disabled={!isEditing}>
                  具有專利權
                </Checkbox>
                <Checkbox checked={f10h_48} onChange={(e) => setF10h48(e.target.checked)} disabled={!isEditing}>
                  獨占性廠商
                </Checkbox>
                <Checkbox checked={f10h_49} onChange={(e) => setF10h49(e.target.checked)} disabled={!isEditing}>
                  股票上市或上櫃
                </Checkbox>
              </div>
            </div>
          </TabPane>

          {/* Tab 5: Approval (Issue 8: renumbered from key="6" to key="5") */}
          <TabPane tab="核准說明" key="5">
            <div className="p110h-section">
              <h4>
                部門主管核准說明
                {f10h_80 === 'P' && <span style={{ color: '#52c41a', marginLeft: 8 }}>(已核准)</span>}
              </h4>
              <TextArea value={f10h_802} disabled rows={4} style={{ maxWidth: 500, marginBottom: 16 }} />

              <h4>
                研發部門核准說明
                {f10h_81 === 'P' && <span style={{ color: '#52c41a', marginLeft: 8 }}>(已核准)</span>}
              </h4>
              <TextArea value={f10h_812} disabled rows={4} style={{ maxWidth: 500, marginBottom: 16 }} />

              <h4>
                總經理核准說明
                {f10h_82 === 'P' && <span style={{ color: '#52c41a', marginLeft: 8 }}>(已核准)</span>}
              </h4>
              <TextArea value={f10h_822} disabled rows={4} style={{ maxWidth: 500 }} />
            </div>
          </TabPane>
        </Tabs>

        {/* SharedActionBar replaces old action buttons */}
        <SharedActionBar
          mode={mode} isEditing={isEditing} hasRecord={recordCount > 0}
          currentIdx={recordPosition - 1} recordCount={recordCount}
          onNavFirst={navFirst} onNavPrev={navPrev}
          onNavNext={navNext} onNavLast={navLast}
          onAdd={startAdd} onModify={startEdit} onDelete={handleDelete}
          onSave={save} onCancel={cancel}
          approval={approval}
          onSubmit={handleSubmit} onApprove={handleApprove}
          onReject={handleReject} onWriteoff={handleWriteoff} onHistory={() => setShowHistory(true)}
          onExit={() => navigate(-1)}
        />
      </div>

      {/* Approval History Modal */}
      {f10h_01 && (
        <ApprovalHistoryModal
          visible={showHistory}
          orderNo={f10h_01}
          onClose={() => setShowHistory(false)}
        />
      )}
    </Spin>
  );
};

export default P110HForm;
